<div>
  <div class="container-fluid">
    <!-- Back Button -->
    <div class="mb-3">
      <button type="button" class="btn btn-outline-secondary" (click)="goBack()">
        <i class="bi bi-arrow-left me-2"></i>
        Back to Drone Inventory
      </button>
      <a *ngIf="drone" class="btn btn-outline-primary ms-2" [routerLink]="['/dji-flights']" [queryParams]="{ droneId: drone.id, droneLabel: drone.serialNumber }">
        <i class="bi bi-airplane me-2"></i>
        DJI flights
      </a>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="text-muted mt-3">Loading drone details...</p>
    </div>

    <!-- Error State -->
    <div *ngIf="errorMessage && !loading" class="alert alert-danger alert-dismissible" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''"></button>
    </div>

    <!-- Success Message -->
    <div *ngIf="syncSuccessMessage" class="alert alert-success alert-dismissible" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ syncSuccessMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="syncSuccessMessage = ''"></button>
    </div>

    <!-- Drone Detail Content -->
    <div *ngIf="drone && !loading">
      <!-- Header Card -->
      <div class="card mb-4">
        <div class="card-body">
          <div class="row align-items-center">
            <div class="col-auto">
              <div class="entity-avatar entity-avatar-lg fs-4">
                <i class="adn-icon adn-icon-drone-solid"></i>
              </div>
            </div>
            <div class="col">
              <div class="d-flex align-items-center gap-3 mb-2">
                <h2 class="fs-4 fw-semibold mb-0">{{ drone.name }}</h2>
                <span class="badge" [ngClass]="getStatusClass(drone.status)">
                  {{ getStatusLabel(drone.status) }}
                </span>
              </div>
              <p class="text-muted mb-0">
                {{ drone.manufacturerName || 'Unknown Manufacturer' }} {{ drone.modelName || '' }}
                <span class="mx-2">•</span>
                <span class="font-monospace">{{ drone.serialNumber }}</span>
              </p>
            </div>
            <div class="col-auto">
              <button type="button" class="btn btn-primary me-2" (click)="editDrone()">
                <i class="bi bi-pencil me-2"></i>
                Edit Drone
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <!-- Left Column - Main Info -->
        <div class="col-lg-8">
          <!-- Drone Information -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="bi bi-info-circle me-2"></i>
                Drone Information
              </h5>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Name</label>
                  <div>{{ drone.name }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Status</label>
                  <div>
                    <span class="badge" [ngClass]="getStatusClass(drone.status)">
                      {{ getStatusLabel(drone.status) }}
                    </span>
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Manufacturer</label>
                  <div>{{ drone.manufacturerName || 'Unknown' }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Model</label>
                  <div>{{ drone.modelName || 'Unknown' }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Serial Number (Aircraft)</label>
                  <div class="font-monospace">{{ drone.serialNumber }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Serial Number (Packaging)</label>
                  <div class="font-monospace">{{ drone.packagingSerialNumber || '—' }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Version</label>
                  <div>{{ drone.version || '—' }}</div>
                </div>
              </div>

              <hr class="my-4">

              <!-- Registration & Identification -->
              <h6 class="text-muted mb-3">
                <i class="bi bi-card-checklist me-2"></i>
                Registration & Identification
              </h6>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">FAA N-Number</label>
                  <div>
                    <span *ngIf="drone.nNumber" class="font-monospace">{{ drone.nNumber }}</span>
                    <span *ngIf="!drone.nNumber" class="text-warning">
                      <i class="bi bi-exclamation-triangle me-1"></i>
                      Not Registered
                    </span>
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">N-Number Active Date</label>
                  <div>{{ drone.nNumberActiveDate ? formatDate(drone.nNumberActiveDate) : '—' }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Remote ID</label>
                  <div class="font-monospace">{{ drone.remoteId || '—' }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Remote Broadcast Module</label>
                  <div class="font-monospace">{{ drone.remoteBroadcastModule || '—' }}</div>
                </div>
              </div>

              <hr class="my-4">

              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Added to Inventory</label>
                  <div>{{ formatDateTime(drone.createdAt) }}</div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Last Updated</label>
                  <div>{{ formatDateTime(drone.updatedAt) }}</div>
                </div>
              </div>

              <hr class="my-4">

              <div class="row">
                <div class="col-md-12 mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Inventory Note</label>
                  <div>{{ drone.inventoryNote || '—' }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Reports Section (Expandable) -->
          <div class="card mb-4">
            <div class="card-header" style="cursor: pointer;" (click)="toggleReportsSection()">
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">
                  <i class="bi bi-bar-chart me-2"></i>
                  Reports
                </h5>
                <i class="bi" [ngClass]="reportsExpanded ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </div>
            </div>
            <div class="card-body" *ngIf="reportsExpanded">
              <!-- No Assignment Message -->
              <div *ngIf="!currentAssignment" class="alert alert-info">
                <i class="bi bi-info-circle me-2"></i>
                Reports are only available when the drone is assigned to an organization.
              </div>

              <!-- Reports Content -->
              <div *ngIf="currentAssignment">
                <!-- Error Message -->
                <div *ngIf="reportErrorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
                  <i class="bi bi-exclamation-triangle-fill me-2"></i>
                  {{ reportErrorMessage }}
                  <button type="button" class="btn-close" aria-label="Close" (click)="reportErrorMessage = ''"></button>
                </div>

                <!-- Date Range Filter -->
                <div class="card mb-4 bg-body-tertiary">
                  <div class="card-body">
                    <div class="row g-3 align-items-end">
                      <div class="col-md-4">
                        <label class="form-label fw-semibold">Start Date</label>
                        <input
                          type="date"
                          class="form-control"
                          [(ngModel)]="reportStartDate"
                          (change)="onReportFilterChange()"
                        />
                      </div>
                      <div class="col-md-4">
                        <label class="form-label fw-semibold">End Date</label>
                        <input
                          type="date"
                          class="form-control"
                          [(ngModel)]="reportEndDate"
                          (change)="onReportFilterChange()"
                        />
                      </div>
                      <div class="col-md-4">
                        <button
                          type="button"
                          class="btn btn-outline-primary w-100"
                          (click)="loadReportData()"
                          [disabled]="loadingReports"
                        >
                          <span *ngIf="loadingReports" class="spinner-border spinner-border-sm me-2"></span>
                          <i *ngIf="!loadingReports" class="bi bi-arrow-clockwise me-2"></i>
                          Refresh Reports
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Loading State -->
                <div *ngIf="loadingReports" class="text-center py-5">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="text-muted mt-2">Loading report data...</p>
                </div>

                <!-- Reports Content -->
                <div *ngIf="!loadingReports">
                  <!-- Acreage Report Section -->
                  <div class="card mb-4">
                    <div class="card-header d-flex justify-content-between align-items-center">
                      <h6 class="mb-0">
                        <i class="bi bi-map me-2"></i>
                        Acreage Report
                      </h6>
                      <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        (click)="exportAcreagePdf()"
                        [disabled]="exportingAcreage || !acreageData"
                      >
                        <span *ngIf="exportingAcreage" class="spinner-border spinner-border-sm me-2"></span>
                        <i *ngIf="!exportingAcreage" class="bi bi-file-earmark-pdf me-2"></i>
                        Export PDF
                      </button>
                    </div>
                    <div class="card-body">
                      <div *ngIf="acreageData">
                        <div class="row mb-3">
                          <div class="col-md-4 text-center">
                            <div class="fs-5 fw-bold lh-sm mb-1">{{ formatNumber(acreageData.totalAcreage) }}</div>
                            <div class="small fw-semibold text-uppercase text-body-secondary">Total Acres Sprayed</div>
                          </div>
                          <div class="col-md-4 text-center">
                            <div class="fs-5 fw-bold lh-sm mb-1">{{ acreageData.totalFlights }}</div>
                            <div class="small fw-semibold text-uppercase text-body-secondary">Total Flights</div>
                          </div>
                          <div class="col-md-4 text-center">
                            <div class="fs-5 fw-bold lh-sm mb-1">
                              {{ formatDate(acreageData.startDate) }} - {{ formatDate(acreageData.endDate) }}
                            </div>
                            <div class="small fw-semibold text-uppercase text-body-secondary">Date Range</div>
                          </div>
                        </div>

                        <!-- Compact Summary Tables -->
                        <div class="row g-3">
                          <div class="col-md-6">
                            <h6 class="text-muted mb-2 small">By Pilot</h6>
                            <div class="table-responsive">
                              <table class="table table-sm table-hover mb-0">
                                <thead>
                                  <tr>
                                    <th>Pilot</th>
                                    <th class="text-end">Acreage</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr *ngIf="acreageData.byPilot.length === 0">
                                    <td colspan="2" class="text-center text-muted">No data</td>
                                  </tr>
                                  <tr *ngFor="let item of acreageData.byPilot">
                                    <td>{{ item.name }}</td>
                                    <td class="text-end">{{ formatNumber(item.totalAcreage) }}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <h6 class="text-muted mb-2 small">By Field</h6>
                            <div class="table-responsive">
                              <table class="table table-sm table-hover mb-0">
                                <thead>
                                  <tr>
                                    <th>Field</th>
                                    <th class="text-end">Acreage</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr *ngIf="acreageData.byField.length === 0">
                                    <td colspan="2" class="text-center text-muted">No data</td>
                                  </tr>
                                  <tr *ngFor="let item of acreageData.byField">
                                    <td>{{ item.name }}</td>
                                    <td class="text-end">{{ formatNumber(item.totalAcreage) }}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div *ngIf="!acreageData" class="text-center text-muted py-4">
                        No acreage data available for selected period
                      </div>
                    </div>
                  </div>

                  <!-- Chemical Usage Report Section -->
                  <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                      <h6 class="mb-0">
                        <i class="bi bi-droplet me-2"></i>
                        Chemical Usage Report
                      </h6>
                      <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        (click)="exportChemicalPdf()"
                        [disabled]="exportingChemical || !chemicalData"
                      >
                        <span *ngIf="exportingChemical" class="spinner-border spinner-border-sm me-2"></span>
                        <i *ngIf="!exportingChemical" class="bi bi-file-earmark-pdf me-2"></i>
                        Export PDF
                      </button>
                    </div>
                    <div class="card-body">
                      <div *ngIf="chemicalData && chemicalData.chemicalUsage.length > 0">
                        <div class="table-responsive">
                          <table class="table table-sm table-hover mb-0">
                            <thead>
                              <tr>
                                <th>Chemical Name</th>
                                <th>EPA Number</th>
                                <th class="text-end">Amount Used</th>
                                <th class="text-end">Flights</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr *ngFor="let chemical of chemicalData.chemicalUsage">
                                <td>{{ chemical.chemicalName }}</td>
                                <td>{{ chemical.epaNumber || '-' }}</td>
                                <td class="text-end">{{ formatNumber(chemical.totalAmountUsed) }} {{ chemical.unit }}</td>
                                <td class="text-end">{{ chemical.flightCount }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div *ngIf="!chemicalData || chemicalData.chemicalUsage.length === 0" class="text-center text-muted py-4">
                        No chemical usage data available for selected period
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Resources Section (Expandable) -->
          <div class="card mb-4">
            <div class="card-header" style="cursor: pointer;" (click)="toggleResourcesSection()">
              <div class="d-flex justify-content-between align-items-center">
                <h5 class="mb-0">
                  <i class="bi bi-file-earmark me-2"></i>
                  Resources
                </h5>
                <i class="bi" [ngClass]="resourcesExpanded ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </div>
            </div>
            <div class="card-body" *ngIf="resourcesExpanded">
              @if (droneResourcesErrorMessage) {
                <div class="alert alert-danger alert-dismissible fade show" role="alert">
                  <i class="bi bi-exclamation-triangle-fill me-2"></i>
                  {{ droneResourcesErrorMessage }}
                  <button type="button" class="btn-close" aria-label="Close" (click)="droneResourcesErrorMessage = ''"></button>
                </div>
              }
              @if (droneResourcesSuccessMessage) {
                <div class="alert alert-success alert-dismissible fade show" role="alert">
                  <i class="bi bi-check-circle-fill me-2"></i>
                  {{droneResourcesSuccessMessage}}
                  <button type="button" class="btn-close" aria-label="Close" (click)="droneResourcesSuccessMessage = ''"></button>
                </div>
              }
              <div class="row g-3 align-items-end mb-3">
                <div class="col-md-4">
                  <label class="form-label fw-semibold">Resource Type</label>
                  <select class="form-select" [(ngModel)]="resourceType" (change)="loadDroneResources()">
                    <!-- Select the kind of resource -->
                    @for(resourceType of resourceTypes; track resourceType) {
                      <option [value]="resourceType">
                        {{ resourceType | removeDash | titlecase }}
                      </option>
                    }
                  </select>
                </div>
                <div class="col-md-4">
                  <label class="form-label fw-semibold">Access</label>
                  <select class="form-select" [(ngModel)]="adminOnly" (change)="loadDroneResources()">
                    <option value="all">All</option>
                    <option value="true">Internal only</option>
                    <option value="false">Shared with operator</option>
                  </select>
                </div>
                <div class="col d-flex justify-content-end">
                  <button
                    type="button"
                    class="btn btn-primary"
                    (click)="openResourceUploadModal()">
                    <i class="bi bi-plus-circle me-2"></i>
                    Attach Resource
                  </button>
                </div>
              </div>

              @if (droneResources.length) {
                <div class="card overflow-hidden">
                  <div class="table-responsive">
                    <table class="table data-table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>File Name</th>
                        <th>Type</th>
                        <th>Admin Only</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for(resource of droneResources; track resource){
                        <tr>
                          <td>
                            <div class="d-flex align-items-center gap-3">
                              <div class="entity-avatar">
                                <i class="bi bi-file-earmark"></i>
                              </div>
                              <div>
                                <div class="fw-medium">{{ resource.fileName }}</div>
                                @if (resource.caption) {
                                  <div class="text-muted">{{ resource.caption }}</div>
                                }
                                <div class="text-muted">{{ formatFileSize(resource.fileSize) }}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span>{{ resource.resourceType | removeDash | titlecase }}</span>
                          </td>
                          <td>
                            @if (resource.adminOnly) {
                              <span class="badge bg-secondary">
                                <i class="bi bi-lock-fill me-1"></i>Internal
                              </span>
                            } @else {
                              <span class="badge bg-info-subtle text-info-emphasis">
                                <i class="bi bi-eye me-1"></i>Shared
                              </span>
                            }
                          </td>
                          <td class="text-end text-nowrap">
                            <div class="d-flex justify-content-end gap-1">
                            <button type="button" title="Download Resource" aria-label="Download Resource" class="btn btn-sm btn-outline-primary" (click)="getDroneResourceFile(resource)">
                              <i class="bi bi-file-earmark-arrow-down"></i>
                            </button>
                            <button type="button" title="Edit" aria-label="Edit" class="btn btn-sm btn-outline-secondary" (click)="openResourceUploadModal(resource)">
                              <i class="bi bi-pencil"></i>
                            </button>
                            <button type="button" title="Delete Resource" aria-label="Delete Resource" class="btn btn-sm btn-danger" (click)="openConfirmDeleteModal(resource)">
                              <i class="bi bi-trash"></i>
                            </button>
                            </div>
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                  </div>
                </div>
              } @else {
                <div class="alert alert-info">
                  <i class="bi bi-info-circle me-2"></i>
                  There are no resources associated with this drone.
                </div>
              }
            </div> 
          </div>  
        </div>

        <!-- Right Column - Sidebar -->
        <div class="col-lg-4">
          <!-- Organization Assignment -->
          <div class="card mb-4">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h5 class="mb-0">
                <i class="bi bi-building me-2"></i>
                Organization Assignment
              </h5>
              <button
                type="button"
                class="btn btn-sm btn-link p-0"
                (click)="openHistoryModal()"
                title="View assignment history"
              >
                <i class="bi bi-clock-history"></i>
              </button>
            </div>
            <div class="card-body">
              <!-- Loading State -->
              <div *ngIf="loadingAssignment" class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>

              <!-- Currently Assigned -->
              <div *ngIf="!loadingAssignment && currentAssignment">
                <div class="mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Assigned To</label>
                  <div>
                    <a [routerLink]="['/organizations', currentAssignment.organizationId]" class="text-decoration-none">
                      <i class="bi bi-building me-1"></i>
                      {{ currentAssignment.organizationName || getOrganizationName(currentAssignment.organizationId) }}
                    </a>
                  </div>
                </div>
                <div class="mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Assigned On</label>
                  <div>{{ formatDateTime(currentAssignment.assignedAt) }}</div>
                </div>
                <div *ngIf="currentAssignment.notes" class="mb-3">
                  <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Notes</label>
                  <div class="small text-muted">{{ currentAssignment.notes }}</div>
                </div>
                <div class="d-grid gap-2">
                  <button
                    type="button"
                    class="btn btn-outline-secondary btn-sm"
                    (click)="openReturnModal()"
                    [disabled]="returning"
                  >
                    <i class="bi bi-box-arrow-in-left me-2"></i>
                    Return Drone
                  </button>
                  <button
                    type="button"
                    class="btn btn-outline-danger btn-sm"
                    (click)="openRevokeModal()"
                    [disabled]="revoking"
                  >
                    <i class="bi bi-x-circle me-2"></i>
                    Revoke Assignment
                  </button>
                </div>
              </div>

              <!-- Not Assigned -->
              <div *ngIf="!loadingAssignment && !currentAssignment">
                <p *ngIf="drone?.status === 'assigned'" class="text-warning small mb-3">
                  <i class="bi bi-exclamation-triangle me-1"></i>
                  This drone is marked as "Assigned" but has no assignment record. Please assign it to an organization.
                </p>
                <p *ngIf="drone?.status !== 'assigned'" class="text-muted small mb-3">
                  <i class="bi bi-info-circle me-1"></i>
                  This drone is not currently assigned to any organization.
                </p>
                <div class="d-grid">
                  <button
                    type="button"
                    class="btn btn-primary btn-sm"
                    (click)="openAssignModal()"
                    [disabled]="assigning || (drone?.status !== 'available' && drone?.status !== 'assigned') || !drone?.nNumber"
                  >
                    <i class="bi bi-plus-lg me-2"></i>
                    Assign to Organization
                  </button>
                </div>
                <p *ngIf="!drone?.nNumber" class="text-danger small mt-2 mb-0">
                  <i class="bi bi-exclamation-triangle me-1"></i>
                  Drone must have an FAA N-Number before it can be assigned.
                </p>
                <p *ngIf="drone?.nNumber && drone?.status !== 'available' && drone?.status !== 'assigned'" class="text-muted small mt-2 mb-0">
                  <i class="bi bi-exclamation-triangle me-1"></i>
                  Drone must be "Available" to assign.
                </p>
              </div>
            </div>
          </div>

          <!-- Status Management -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="bi bi-toggles me-2"></i>
                Status Management
              </h5>
            </div>
            <div class="card-body">
              <p class="text-muted small mb-3">Change the drone's current status:</p>
              <div class="d-grid gap-2">
                <button
                  type="button"
                  class="btn btn-sm"
                  [ngClass]="drone.status === 'available' ? 'btn-success' : 'btn-outline-success'"
                  (click)="updateStatus('available')"
                  [disabled]="drone.status === 'available'"
                >
                  <i class="bi bi-check-circle me-2"></i>
                  Available
                </button>
                <button
                  type="button"
                  class="btn btn-sm"
                  [ngClass]="drone.status === 'assigned' ? 'btn-primary' : 'btn-outline-primary'"
                  (click)="updateStatus('assigned')"
                  [disabled]="drone.status === 'assigned'"
                >
                  <i class="bi bi-person-check me-2"></i>
                  Assigned
                </button>
                <button
                  type="button"
                  class="btn btn-sm"
                  [ngClass]="drone.status === 'maintenance' ? 'btn-warning' : 'btn-outline-warning'"
                  (click)="updateStatus('maintenance')"
                  [disabled]="drone.status === 'maintenance'"
                >
                  <i class="bi bi-wrench me-2"></i>
                  Maintenance
                </button>
                <button
                  type="button"
                  class="btn btn-sm"
                  [ngClass]="drone.status === 'retired' ? 'btn-secondary' : 'btn-outline-secondary'"
                  (click)="updateStatus('retired')"
                  [disabled]="drone.status === 'retired'"
                >
                  <i class="bi bi-archive me-2"></i>
                  Retired
                </button>
              </div>
            </div>
          </div>

          <!-- Zoho Sync -->
          <div class="card mb-4">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="bi bi-cloud me-2"></i>
                Zoho FSM Sync
              </h5>
            </div>
            <div class="card-body">
              <div class="mb-3">
                <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Sync Status</label>
                <div>
                  <span class="badge" [ngClass]="getZohoSyncStatusClass()">
                    {{ getZohoSyncStatusLabel() }}
                  </span>
                </div>
              </div>
              <div *ngIf="drone.zohoFsmProductId" class="mb-3">
                <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Zoho FSM Product ID</label>
                <div class="font-monospace small">{{ drone.zohoFsmProductId }}</div>
              </div>
              <div *ngIf="drone.zohoFsmSyncedAt" class="mb-3">
                <label class="d-block small fw-semibold text-uppercase text-body-secondary mb-1">Last FSM Sync</label>
                <div>{{ formatDateTime(drone.zohoFsmSyncedAt) }}</div>
              </div>
              <div *ngIf="drone.zohoFsmSyncError" class="mb-3">
                <label class="d-block small fw-semibold text-uppercase text-danger mb-1">FSM Sync Error</label>
                <div class="text-danger small">{{ drone.zohoFsmSyncError }}</div>
              </div>
              <div class="d-grid">
                <button
                  type="button"
                  class="btn"
                  [ngClass]="drone.zohoFsmSyncStatus === 'synced' ? 'btn-outline-primary' : 'btn-primary'"
                  (click)="syncToZoho()"
                  [disabled]="syncing"
                >
                  <span *ngIf="syncing" class="spinner-border spinner-border-sm me-2"></span>
                  <i *ngIf="!syncing" class="bi bi-cloud-arrow-up me-2"></i>
                  {{ syncing ? 'Syncing...' : (drone.zohoFsmSyncStatus === 'synced' ? 'Re-sync to Zoho' : 'Sync to Zoho') }}
                </button>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="card">
            <div class="card-header">
              <h5 class="mb-0">
                <i class="bi bi-gear me-2"></i>
                Actions
              </h5>
            </div>
            <div class="card-body">
              <div class="d-grid gap-2">
                <button type="button" class="btn btn-primary" (click)="editDrone()">
                  <i class="bi bi-pencil me-2"></i>
                  Edit Drone
                </button>
                <button
                  type="button"
                  class="btn btn-outline-danger"
                  (click)="confirmArchive()"
                  [disabled]="archiving"
                >
                  <span *ngIf="archiving" class="spinner-border spinner-border-sm me-2"></span>
                  <i *ngIf="!archiving" class="bi bi-archive me-2"></i>
                  {{ archiving ? 'Archiving...' : 'Archive Drone' }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Archive Confirmation Modal -->
<div class="modal fade" id="archiveModal" tabindex="-1" aria-labelledby="archiveModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="archiveModalLabel">
          <i class="bi bi-exclamation-triangle text-warning me-2"></i>
          Archive Drone
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p>Are you sure you want to archive <strong>{{ drone?.name }}</strong>?</p>
        <p class="text-muted mb-0">
          This will remove the drone from the active inventory. The drone can be restored later if needed.
        </p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
        <button type="button" class="btn btn-danger" (click)="archiveDrone()" [disabled]="archiving">
          <span *ngIf="archiving" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!archiving" class="bi bi-archive me-2"></i>
          {{ archiving ? 'Archiving...' : 'Archive' }}
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Assign to Organization Modal -->
<div class="modal fade" id="assignModal" tabindex="-1" aria-labelledby="assignModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="assignModalLabel">
          <i class="bi bi-building me-2"></i>
          Assign Drone to Organization
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p class="text-muted mb-3">
          Assign <strong>{{ drone?.name }}</strong> to an organization.
        </p>
        <div class="mb-3">
          <label for="organizationSearch" class="form-label">Organization <span class="text-danger">*</span></label>
          <div class="position-relative">
            <!-- Selected organization display -->
            <div *ngIf="selectedOrganization" class="form-control d-flex align-items-center justify-content-between bg-body-tertiary border-primary">
              <span>
                <i class="bi bi-building me-2"></i>
                {{ selectedOrganization.name }}
              </span>
              <button
                type="button"
                class="btn btn-sm btn-link text-muted p-0"
                (click)="clearOrganizationSelection()"
                title="Clear selection"
              >
                <i class="bi bi-x-lg"></i>
              </button>
            </div>

            <!-- Search input (shown when no selection) -->
            <div *ngIf="!selectedOrganization" class="input-group">
              <span class="input-group-text">
                <i class="bi bi-search"></i>
              </span>
              <input
                type="text"
                id="organizationSearch"
                class="form-control"
                placeholder="Type to search organizations..."
                [(ngModel)]="organizationSearchQuery"
                (input)="onOrganizationSearchInput()"
                (focus)="onOrganizationSearchFocus()"
                (blur)="onOrganizationSearchBlur()"
                [disabled]="loadingOrganizations"
                autocomplete="off"
              />
            </div>

            <!-- Dropdown results -->
            <div
              *ngIf="showOrganizationDropdown && !selectedOrganization"
              class="dropdown-menu show w-100 mt-1 shadow-sm"
              style="max-height: 250px; overflow-y: auto; z-index: 1000;"
            >
              <!-- Loading state -->
              <div *ngIf="loadingOrganizations" class="p-3 text-center text-muted">
                <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                Loading organizations...
              </div>

              <!-- No results -->
              <div *ngIf="!loadingOrganizations && filteredOrganizations.length === 0" class="p-3 text-center text-muted">
                <i class="bi bi-inbox me-1"></i>
                No organizations found
              </div>

              <!-- Results list -->
              <div
                *ngFor="let org of filteredOrganizations"
                class="dropdown-item px-3 py-2 cursor-pointer"
                (mousedown)="selectOrganization(org)"
              >
                <i class="bi bi-building me-2 text-muted"></i>
                {{ org.name }}
              </div>
            </div>
          </div>
          <small *ngIf="organizations.length > 0 && !selectedOrganization" class="text-muted">
            {{ organizations.length }} organizations available
          </small>
        </div>
        <div class="mb-3">
          <label for="assignmentNotes" class="form-label">Notes (optional)</label>
          <textarea
            id="assignmentNotes"
            class="form-control"
            rows="2"
            [(ngModel)]="assignmentNotes"
            placeholder="Add any notes about this assignment..."
          ></textarea>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
        <button
          type="button"
          class="btn btn-primary"
          (click)="assignDrone()"
          [disabled]="assigning || !selectedOrganizationId"
        >
          <span *ngIf="assigning" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!assigning" class="bi bi-check-lg me-2"></i>
          {{ assigning ? 'Assigning...' : 'Assign Drone' }}
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Return Drone Modal -->
<div class="modal fade" id="returnModal" tabindex="-1" aria-labelledby="returnModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="returnModalLabel">
          <i class="bi bi-box-arrow-in-left me-2"></i>
          Return Drone
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p>
          Return <strong>{{ drone?.name }}</strong> from
          <strong>{{ currentAssignment?.organizationName || getOrganizationName(currentAssignment?.organizationId || '') }}</strong>?
        </p>
        <div class="mb-3">
          <label for="returnNotes" class="form-label">Notes (optional)</label>
          <textarea
            id="returnNotes"
            class="form-control"
            rows="2"
            [(ngModel)]="returnNotes"
            placeholder="Add any notes about this return..."
          ></textarea>
        </div>
        <p class="text-muted small mb-0">
          The drone will be marked as "Available" and can be assigned to another organization.
        </p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
        <button type="button" class="btn btn-primary" (click)="returnDrone()" [disabled]="returning">
          <span *ngIf="returning" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!returning" class="bi bi-box-arrow-in-left me-2"></i>
          {{ returning ? 'Returning...' : 'Return Drone' }}
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Revoke Assignment Modal -->
<div class="modal fade" id="revokeModal" tabindex="-1" aria-labelledby="revokeModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="revokeModalLabel">
          <i class="bi bi-x-circle text-danger me-2"></i>
          Revoke Assignment
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p>
          Revoke the assignment of <strong>{{ drone?.name }}</strong> from
          <strong>{{ currentAssignment?.organizationName || getOrganizationName(currentAssignment?.organizationId || '') }}</strong>?
        </p>
        <div class="mb-3">
          <label for="revokeReason" class="form-label">Reason <span class="text-danger">*</span></label>
          <textarea
            id="revokeReason"
            class="form-control"
            rows="2"
            [(ngModel)]="revokeReason"
            placeholder="Provide a reason for revoking this assignment..."
            required
          ></textarea>
        </div>
        <div class="alert alert-warning small mb-0">
          <i class="bi bi-exclamation-triangle me-1"></i>
          This is an administrative action. A reason is required for audit purposes.
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
        <button
          type="button"
          class="btn btn-danger"
          (click)="revokeDrone()"
          [disabled]="revoking || !revokeReason.trim()"
        >
          <span *ngIf="revoking" class="spinner-border spinner-border-sm me-2"></span>
          <i *ngIf="!revoking" class="bi bi-x-circle me-2"></i>
          {{ revoking ? 'Revoking...' : 'Revoke Assignment' }}
        </button>
      </div>
    </div>
  </div>
</div>

<!-- Assignment History Modal -->
<div class="modal fade" id="historyModal" tabindex="-1" aria-labelledby="historyModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered modal-lg">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title" id="historyModalLabel">
          <i class="bi bi-clock-history me-2"></i>
          Assignment History
        </h5>
        <button type="button" class="btn-close" aria-label="Close" data-bs-dismiss="modal" aria-label="Close"></button>
      </div>
      <div class="modal-body">
        <p class="text-muted mb-3">
          Assignment history for <strong>{{ drone?.name }}</strong>
        </p>

        <!-- No History -->
        <div *ngIf="assignmentHistory.length === 0" class="text-center py-4 text-muted">
          <i class="bi bi-inbox fs-1 d-block mb-2"></i>
          <p>No assignment history found.</p>
        </div>

        <!-- History Table -->
        <div *ngIf="assignmentHistory.length > 0" class="table-responsive">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Organization</th>
                <th>Assigned</th>
                <th>Returned</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let assignment of assignmentHistory">
                <td>{{ assignment.organizationName || getOrganizationName(assignment.organizationId) }}</td>
                <td>{{ formatDate(assignment.assignedAt) }}</td>
                <td>{{ assignment.returnedAt ? formatDate(assignment.returnedAt) : '—' }}</td>
                <td>
                  <span class="badge" [ngClass]="getAssignmentStatusClass(assignment.status)">
                    {{ getAssignmentStatusLabel(assignment.status) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
      </div>
    </div>
  </div>

</div>

@if (showResourceUploadModal) {
  <app-drone-resource-upload-modal
    [droneId]="drone?.id"
    [resource]="editingResource"
    (close)="closeResourceUploadModal()"
    (newItem)="onResourceAdded($event)"
    (updatedItem)="onResourceUpdated($event)">
  </app-drone-resource-upload-modal>
}

@if (showConfirmDeleteModal) {
  <app-confirmation-modal
    title="Delete Drone Resource"
    message="Are you sure you want to delete resource: '{{resourceToDelete?.fileName}}'"
    confirmationLabel="Delete"
    variant="danger"
    [processing]="deletingResource"
    (confirmed)="onResourceDeleteConfirm()"
    (cancelled)="showConfirmDeleteModal = false">
  </app-confirmation-modal>
}
